import {
  Suspense, createContext, isValidElement, lazy, useContext, useEffect, useState,
} from 'react';
import './App.css';
import {
  BrowserRouter, Routes, Route, Navigate, useLocation,
} from 'react-router-dom';
import client from './lib/api';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import { captureRef } from './lib/referral';
import { brandName } from './lib/brand';
import Header from './components/Header';
import Footer from './components/Footer';
import LiveChatSupport from './components/LiveChatSupport';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from './components/ui/toaster';
import CustomCodeInjector from './components/CustomCodeInjector';
import WelcomePopup from './components/WelcomePopup';
import PlayerMessageAlerts from './components/PlayerMessageAlerts';
import DomainRedirect from './components/DomainRedirect';
import PushSubscribe from './components/PushSubscribe';
import PageMarker from './components/PageMarker';
import BetrunLegacyPage from './components/BetrunLegacyPage';
import ViewportLock from './components/ViewportLock';
import { SHELL_IDS } from './lib/domHooks';
import BrowserProtection from './components/BrowserProtection';
import ServiceUnavailableScreen from './components/ServiceUnavailableScreen';
import DomainNotFoundScreen from './components/DomainNotFoundScreen';
import {
  adminRoutesVisible, portalVisible,
} from './lib/adminDomain';

const AsusLayout = lazy(() => import('./themes/asus/AsusLayout'));
const AsusHome = lazy(() => import('./themes/asus/AsusHome'));
const AsusCatalog = lazy(() => import('./themes/asus/AsusCatalog'));
const BetrunLayout = lazy(() => import('./themes/betrun/BetrunLayout'));
const BetrunHome = lazy(() => import('./themes/betrun/BetrunHome'));
const ZumrutLayout = lazy(() => import('./themes/zumrut/ZumrutLayout'));
const ZumrutHome = lazy(() => import('./themes/zumrut/ZumrutHome'));
const ParisLayout = lazy(() => import('./themes/paris/ParisLayout'));
const ParisHome = lazy(() => import('./themes/paris/ParisHome'));
const HerkulLayout = lazy(() => import('./themes/herkul/HerkulLayout'));
const HerkulHome = lazy(() => import('./themes/herkul/HerkulHome'));
const HerkulSportsPage = lazy(() => import('./themes/herkul/HerkulSportsPage'));
const VegaLayout = lazy(() => import('./themes/vega/VegaLayout'));
const VegaHome = lazy(() => import('./themes/vega/VegaHome'));
// Spinlab tembel yüklenir: kodu ve stili yalnızca şema seçiliyken iner.
import {
  SpinlabAccountFrame, SpinlabHome, SpinlabLayout, SpinlabPage as SpinlabPageFrame, SpinlabRoute,
} from './themes/spinlab';
const SportsbookFrame = lazy(() => import('./pages/SportsbookFrame'));
const SportsLoginGate = lazy(() => import('./components/SportsLoginGate'));
import { sportsGuestViewAllowed } from './lib/sportsAccess';
import { hostedSportsbooks } from './lib/sportsbookProviders';
import { useActiveTheme } from './lib/activeTheme';
import { spinlabAccountTarget } from './lib/accountPaths';

const Home = lazy(() => import('./pages/Home'));
const Slots = lazy(() => import('./pages/Slots'));
const LiveCasino = lazy(() => import('./pages/LiveCasino'));
const Crash = lazy(() => import('./pages/Crash'));
const Sports = lazy(() => import('./pages/Sports'));
const Promotions = lazy(() => import('./pages/Promotions'));
const PromoCode = lazy(() => import('./pages/PromoCode'));
const Wheel = lazy(() => import('./pages/Wheel'));
const Raffle = lazy(() => import('./pages/Raffle'));
const Prediction = lazy(() => import('./pages/Prediction'));
const NotFound = lazy(() => import('./pages/NotFound'));
const BuilderPage = lazy(() => import('./pages/BuilderPage'));
const BuilderRenderer = lazy(() => import('./components/builder/BuilderRenderer'));
const BuilderPreview = lazy(() => import('./pages/BuilderPreview'));
const BuilderLayerHost = lazy(() => import('./components/builder/LayerHost'));
const FooterPage = lazy(() => import('./pages/FooterPage'));
const GamePage = lazy(() => import('./pages/GamePage'));
const AccountLayout = lazy(() => import('./pages/AccountLayout'));
const Profile = lazy(() => import('./pages/account/Profile'));
const KycVerification = lazy(() => import('./pages/account/KycVerification'));
const Security = lazy(() => import('./pages/account/Security'));
const AccessBlockedNotice = lazy(() => import('./components/AccessBlockedNotice'));
const Deposit = lazy(() => import('./pages/account/Deposit'));
const DepositPayment = lazy(() => import('./pages/account/DepositPayment'));
const Withdraw = lazy(() => import('./pages/account/Withdraw'));
const WithdrawalAccount = lazy(() => import('./pages/account/WithdrawalAccount'));
const Messages = lazy(() => import('./pages/account/Messages'));
const GameHistory = lazy(() => import('./pages/account/GameHistory'));
const AccountActivity = lazy(() => import('./pages/account/AccountActivity'));
const BetCoupons = lazy(() => import('./pages/account/BetCoupons'));
const LossBonus = lazy(() => import('./pages/account/LossBonus'));
const MyBonuses = lazy(() => import('./pages/account/MyBonuses'));
const Affiliate = lazy(() => import('./pages/account/Affiliate'));
const PartnerPortal = lazy(() => import('./pages/partner/PartnerPortal'));
import {
  isPartnerHost, partnerPanelPath, partnerPathRedirectUrl, partnerRedirectUrl, DEFAULT_PARTNER_PATH,
} from './lib/partnerPanel';
// Affiliates (BetConstruct Affiliates / Gene UI düzeni) — admin/affiliates/gene.
const AffDashboard = lazy(() => import('./admin/affiliates/gene/Dashboard'));
const AffUsers = lazy(() => import('./admin/affiliates/gene/Network').then((m) => ({ default: m.Users })));
const AffGroups = lazy(() => import('./admin/affiliates/gene/Network').then((m) => ({ default: m.Groups })));
const AffDeleted = lazy(() => import('./admin/affiliates/gene/Network').then((m) => ({ default: m.DeletedUsers })));
const AffTransfers = lazy(() => import('./admin/affiliates/gene/Network').then((m) => ({ default: m.TransferredList })));
const AffUserDetail = lazy(() => import('./admin/affiliates/gene/UserDetail'));
const AffPreview = lazy(() => import('./admin/affiliates/gene/Preview'));
const AffPlayers = lazy(() => import('./admin/affiliates/gene/Players'));
const AffLinks = lazy(() => import('./admin/affiliates/gene/Tools').then((m) => ({ default: m.LinkBuilder })));
const AffSources = lazy(() => import('./admin/affiliates/gene/Tools').then((m) => ({ default: m.Sources })));
const AffLanding = lazy(() => import('./admin/affiliates/gene/Tools').then((m) => ({ default: m.Landing })));
const AffMedia = lazy(() => import('./admin/affiliates/gene/Tools').then((m) => ({ default: m.Media })));
const AffNews = lazy(() => import('./admin/affiliates/gene/Tools').then((m) => ({ default: m.News })));
const AffFeed = lazy(() => import('./admin/affiliates/gene/Tools').then((m) => ({ default: m.DataFeed })));
const AffWithdraws = lazy(() => import('./admin/affiliates/gene/Misc').then((m) => ({ default: m.Withdrawals })));
const AffFilters = lazy(() => import('./admin/affiliates/gene/Misc').then((m) => ({ default: m.FeedFilters })));
const AffNewsletter = lazy(() => import('./admin/affiliates/gene/Misc').then((m) => ({ default: m.Newsletter })));
const AffFiles = lazy(() => import('./admin/affiliates/gene/Misc').then((m) => ({ default: m.FileHub })));
const AffReports = lazy(() => import('./admin/affiliates/gene/Reports'));
const AffConfig = lazy(() => import('./admin/affiliates/gene/Config'));
// CRM (BetConstruct CRM düzeni) tembel yüklenir; bkz. crm/CrmRoutes.jsx.
const CrmRoutes = lazy(() => import('./crm/CrmRoutes'));

const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const Dashboard = lazy(() => import('./admin/Dashboard'));
const ReportPage = lazy(() => import('./admin/reports/ReportPage'));
const PlayerProfile = lazy(() => import('./admin/player/PlayerProfile'));
const PlayerSearch = lazy(() => import('./admin/player/PlayerSearch'));
const PaymentMethods = lazy(() => import('./admin/PaymentMethods'));
const PaymentTransactions = lazy(() => import('./admin/payments/PaymentTransactions'));
const TipsPage = lazy(() => import('./admin/TipsPage'));
const PendingWinsPage = lazy(() => import('./admin/finance/PendingWins'));
const PaymentOrders = lazy(() => import('./admin/payments/PaymentOrders'));
const BonusList = lazy(() => import('./admin/bonus-engine/BonusList'));

const BonusClaims = lazy(() => import('./admin/bonus-engine/BonusClaims'));
const Games = lazy(() => import('./admin/Games'));
const LiveCasinoControl = lazy(() => import('./admin/LiveCasinoControl'));
const SportsBets = lazy(() => import('./admin/SportsBets'));
const SportsBetDetail = lazy(() => import('./admin/SportsBetDetail'));
const SportsbookBetDetail = lazy(() => import('./admin/SportsbookBetDetail'));
const AdminSecurity = lazy(() => import('./admin/Security'));
const CallRequests = lazy(() => import('./admin/CallRequests'));
const PromoCodes = lazy(() => import('./admin/PromoCodes'));
const AuditLog = lazy(() => import('./admin/AuditLog'));
const StaffUsers = lazy(() => import('./admin/staff/StaffUsers'));
const StaffUserDetail = lazy(() => import('./admin/staff/StaffUserDetail'));
const StaffRoles = lazy(() => import('./admin/staff/StaffRoles'));
const StaffRoleDetail = lazy(() => import('./admin/staff/StaffRoleDetail'));
const LoginHistory = lazy(() => import('./admin/staff/LoginHistory'));
const BackupPage = lazy(() => import('./admin/MovedSettingsPages').then((m) => ({ default: m.BackupPage })));
const PaymentIntegrationPage = lazy(() => import('./admin/MovedSettingsPages').then((m) => ({ default: m.PaymentIntegrationPage })));
const ThemePage = lazy(() => import('./admin/cms/ThemePage'));
const Liabilities = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.Liabilities })));
const LiveReservations = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.LiveReservations })));
const SubscriptionAdmin = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.SubscriptionAdmin })));
const MarketGroups = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.MarketGroups })));
const MarketReservation = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.MarketReservation })));
const Orders = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.Orders })));
const GlobalLimits = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.GlobalLimits })));
const SportLimits = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.SportLimits })));
const CasinoLimits = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.CasinoLimits })));
const PopularManage = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.PopularManage })));
const PopularMatches = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.PopularMatches })));
const SuperBets = lazy(() => import('./admin/sportsbook/SportsbookPages').then((m) => ({ default: m.SuperBets })));
const EmailTemplates = lazy(() => import('./admin/messages/MessagesPages').then((m) => ({ default: m.EmailTemplates })));
const MessageList = lazy(() => import('./admin/messages/MessagesPages').then((m) => ({ default: m.MessageList })));
const NewMessage = lazy(() => import('./admin/messages/MessagesPages').then((m) => ({ default: m.NewMessage })));
const SmsTemplates = lazy(() => import('./admin/messages/MessagesPages').then((m) => ({ default: m.SmsTemplates })));
const PartnerSettings = lazy(() => import('./admin/tools/PartnerSettings'));
const PartnerDomains = lazy(() => import('./admin/tools/PartnerDomains'));
const EventsPage = lazy(() => import('./admin/events/EventsPage'));
const Technical = lazy(() => import('./admin/Technical'));
const AdminLogin = lazy(() => import('./admin/AdminLogin'));
const CasinoPage = lazy(() => import('./admin/cms/CasinoPage'));
const PageBuilder = lazy(() => import('./admin/cms/PageBuilder'));
const BannersPage = lazy(() => import('./admin/cms/ContentPages').then((m) => ({ default: m.BannersPage })));
const PromotionsPage = lazy(() => import('./admin/cms/ContentPages').then((m) => ({ default: m.PromotionsPage })));
const MediaPage = lazy(() => import('./admin/cms/SitePages').then((m) => ({ default: m.MediaPage })));
const PagesPage = lazy(() => import('./admin/cms/SitePages').then((m) => ({ default: m.PagesPage })));
const MenusPage = lazy(() => import('./admin/cms/MenusPage'));
const CssPage = lazy(() => import('./admin/cms/SettingsPages').then((m) => ({ default: m.CssPage })));
const LogoWebPage = lazy(() => import('./admin/cms/SettingsPages').then((m) => ({ default: m.LogoWebPage })));
const PaymentsLogoPage = lazy(() => import('./admin/cms/SettingsPages').then((m) => ({ default: m.PaymentsLogoPage })));
const BonusWalletPanel = lazy(() => import('./components/wallet/BonusWalletPanel'));

// CMS içinde eski panel ekranlarını çizen kap: o ekranlar `.admin-*`
// tokenlarına dayanır, CMS kabuğu ise BC CMS görünümündedir.

// Asus'ta temanın kendi işaretlemesiyle çizilen sayfalar (bkz. PublicLayout).
const ASUS_NATIVE = new Set([AsusHome, AsusCatalog]);

// Katalog sayfaları: Asus'ta BetConstruct lobisi, diğer şemalarda ortak sayfa.
const CatalogRoute = ({ category, children }) => {
  const themeId = useActiveTheme();
  if (themeId === 'asus') return <PublicLayout><AsusCatalog category={category} /></PublicLayout>;
  return <PublicLayout>{children}</PublicLayout>;
};

// Spinlab kabuğu rotaların üstünde bir kez kurulduğunda (bkz. AppRoutes)
// PublicLayout yalnızca sayfayı sarar. Eskiden her rota kendi kabuğunu
// çiziyordu; ana sayfa `HomeRoute` içinden çizildiği için React onu başka bir
// ağaç konumu sayıyor, ana sayfaya her dönüşte kabuk (sohbet, başlık
// istekleri, Swiper'lar) sökülüp yeniden kuruluyordu.
const PersistentShell = createContext(false);

const PublicLayout = ({ children }) => {
  const settings = useSettings();
  const themeId = useActiveTheme();
  const persistent = useContext(PersistentShell);

  if (settings.maintenance_mode) {
    return (
      <main className="min-h-screen grid place-items-center px-4 bg-[var(--pusu-bg-deep)]">
        <section className="pusu-panel max-w-lg w-full p-8 text-center">
          <div className="text-3xl font-black gold-text mb-3">{brandName(settings)}</div>
          <h1 className="text-xl font-bold mb-2">Kısa bir bakımdayız</h1>
          <p className="text-sm text-gray-400">Size daha iyi hizmet verebilmek için sistem üzerinde çalışıyoruz. Lütfen kısa süre sonra tekrar deneyin.</p>
          {settings.contact_email && <a className="inline-block mt-5 text-sm gold-text" href={`mailto:${settings.contact_email}`}>{settings.contact_email}</a>}
        </section>
      </main>
    );
  }

  if (persistent && themeId === 'spinlab') return <SpinlabPageFrame>{children}</SpinlabPageFrame>;

  // Hazır tasarım şeması kendi kabuğunu getirir: Asus'ta BetConstruct üst
  // başlığı ve yatay ürün menüsü; Zümrüt'te başlıklı geniş sol menü ve
  // beş sütunlu footer; Paris'te iki katlı üst menü ve yuvarlak köşeli footer;
  // varsayılanda klasik header/footer.
  if (themeId === 'asus') {
    // Ana sayfa ve katalog temanın kendi işaretlemesini kullanır; diğer
    // sayfalar ortak bileşendir ve `.asus-foreign` adasında çizilir.
    const native = isValidElement(children) && ASUS_NATIVE.has(children.type);
    return (
      <AsusLayout native={native} extras={<><ScrollToTop /><LiveChatSupport /></>}>
        {children}
      </AsusLayout>
    );
  }

  // Betrun kuruluma özel şemadır (panelde listelenmez, bkz. lib/siteThemes.js):
  // Rail + üst bar kabuğudur ve diğer şemalardan bağımsız değiştirilir.
  if (themeId === 'betrun') {
    return (
      <BetrunLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </BetrunLayout>
    );
  }

  if (themeId === 'zumrut') {
    return (
      <ZumrutLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </ZumrutLayout>
    );
  }

  if (themeId === 'paris') {
    return (
      <ParisLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </ParisLayout>
    );
  }

  if (themeId === 'herkul') {
    return (
      <HerkulLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </HerkulLayout>
    );
  }

  // Spinlab kendi kabuğunu ve stilini getirir; ortak sayfalar kabuğun
  // içinde `.spl-foreign` adasında çizilir (bkz. themes/spinlab).
  if (themeId === 'spinlab') {
    return (
      // Kaynak temanın kendi destek düğmesi vardır ve sayfa pencere değil
      // `#scroll-container` içinde kayar: sitenin "başa dön" düğmesi ve
      // canlı destek başlatıcısı çizilmez (widget'ın kendisi yüklenir).
      <SpinlabLayout extras={<LiveChatSupport />}>
        {children}
      </SpinlabLayout>
    );
  }

  if (themeId === 'vega') {
    return (
      <VegaLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </VegaLayout>
    );
  }

  return (
    <>
      <Header />
      {/* Klasik kabukta içeriğin kendi sarmalayıcısı yoktu; beş şemada olan
          `#bhs-main` kimliği burada da bulunmalıdır, yoksa panelden yazılan
          kural şema değişince sessizce çalışmaz olurdu. */}
      <div id={SHELL_IDS.main}>{children}</div>
      <Footer />
      <ScrollToTop />
      <LiveChatSupport />
    </>
  );
};

// Ana sayfanın vitrin düzeni de şemaya bağlıdır.
const THEME_HOMES = {
  asus: AsusHome, zumrut: ZumrutHome, paris: ParisHome, herkul: HerkulHome,
  vega: VegaHome, betrun: BetrunHome, spinlab: SpinlabHome,
};

// Spor sayfası normalde tek koddur ve şema onu CSS ile kendi geometrisine
// uydurur. Herkül bilinçli bir istisnadır: referans sportsbook'un düzeni
// (spor listesi rayı + araç paneli) ortak sayfanınkinden tümüyle farklı.
// Veri ve kupon mantığı yine ortak `useSportsbook` hook'undadır.
const THEME_SPORTS = {
  herkul: HerkulSportsPage,
};

const HomeRoute = () => {
  const ThemeHome = THEME_HOMES[useActiveTheme()] || Home;
  const settings = useSettings();
  // Page Builder'da ana sayfa seçildiyse "/" o sayfayı çizer; içerik gelene
  // kadar boş kalır, sayfa yayından kalkmışsa şemanın ana sayfasına dönülür.
  const [builderHome, setBuilderHome] = useState(undefined);
  const wantsBuilder = Boolean(settings.builder_home);
  useEffect(() => {
    if (!wantsBuilder) { setBuilderHome(null); return undefined; }
    let alive = true;
    client.get('/builder/home').then(({ data }) => { if (alive) setBuilderHome(data?.active ? data : null); })
      .catch(() => { if (alive) setBuilderHome(null); });
    return () => { alive = false; };
  }, [wantsBuilder]);
  if (wantsBuilder && builderHome === undefined) return <PublicLayout><div style={{ minHeight: '60vh' }} /></PublicLayout>;
  if (builderHome) return <PublicLayout><BuilderRenderer sections={builderHome.sections || []} /></PublicLayout>;
  return <PublicLayout><ThemeHome /></PublicLayout>;
};

// Spor sayfası sitenin kabuğu içinde açılır (header + footer): oyuncu bakiyesini
// ve menüyü kaybetmeden bahis yapar. Kabuğun dar genişlik sınırı sayfa açıkken
// `body.sb-page` ile kaldırılır — sportsbook üç sütun taşır (bkz. sports.css).
const SportsRoute = () => {
  const settings = useSettings();
  const { user, ready } = useAuth();

  if (!settings.settingsLoaded || !ready) return null;
  if (settings.sports_enabled === false) return <Navigate to="/" replace />;
  // Spor sayfası giriş yapmadan görülebilir mi: karar tek yerdedir ve üç
  // kaynağın (sitenin kendi beslemesi, Betby, Flexrix) hepsinde aynıdır.
  // Kapalıyken ziyaretçi giriş kartını görür; sayfa menüden kaldırılmaz —
  // kaybolan bir bölüm oyuncuya "site bozuldu" gibi görünüyor.
  if (!user && !sportsGuestViewAllowed(settings)) {
    return <PublicLayout><SportsLoginGate /></PublicLayout>;
  }
  // Hazır spor kitabı açıksa /spor onu çizer: sağlayıcı bahsin tamamını
  // (oran, kupon, sonuçlandırma) kendi tarafında yürütür ve sitenin kendi
  // sportsbook'uyla yan yana durması oyuncuya iki ayrı bahis dünyası
  // gösterirdi. Hepsi kapalıyken sayfa sitenin kendi oran beslemesine geri
  // döner. Hangilerinin yayında olduğunu sunucu söyler; birden çoksa oyuncu
  // çerçevenin üstündeki şeritten geçiş yapar.
  const hosted = hostedSportsbooks(settings);
  if (hosted.length) {
    return (
      <PublicLayout>
        <SportsbookFrame providers={hosted} />
      </PublicLayout>
    );
  }
  const ThemeSports = THEME_SPORTS[settings.site_theme] || Sports;
  return <PublicLayout><ThemeSports /></PublicLayout>;
};

const AffiliateAccessRoute = () => {
  const { user, ready } = useAuth();
  const themeId = useActiveTheme();
  if (!ready) return null;
  // Spinlab'da ortaklık başvurusu, durumu ve çekimi kendi sayfasındadır.
  if (themeId === 'spinlab') return <Navigate to="/account/affiliates" replace />;
  if (!user?.can_use_affiliate) {
    return <Navigate to="/hesap/profil" replace />;
  }
  return <Affiliate />;
};

// Spinlab şemasında eski hesap adreslerinin karşılığı (09.10.2026). Eski
// `/hesap/...` sayfaları Spinlab kabuğunda Betrun görünümüyle açılıyordu;
// şema Spinlab iken her biri Spinlab'ın `/account/...` adresine yönlenir
// (sorgu dizesi korunur: ödeme sayfası `?method=&amount=` taşır). Kendi
// Spinlab sayfası olanlar oraya, olmayanlar (para yatırma/çekme, mesajlar …)
// aynı bileşenle Spinlab hesap çerçevesine gider. Diğer şemalar etkilenmez.
const ThemedAccountRoute = ({ page, children }) => {
  const themeId = useActiveTheme();
  const { search } = useLocation();
  const target = themeId === 'spinlab' ? spinlabAccountTarget(page, search) : null;
  if (target) return <Navigate to={target} replace />;
  return children;
};

// Spinlab hesap çerçevesinde çizilen ortak sayfalar (KYC kapıları aynıdır).
const SPINLAB_LEGACY_ACCOUNT = [
  ['/account/deposit', () => <KycProtectedRoute deposit><Deposit /></KycProtectedRoute>],
  ['/account/deposit/payment', () => <KycProtectedRoute deposit><DepositPayment /></KycProtectedRoute>],
  ['/account/withdraw', () => <KycProtectedRoute><Withdraw /></KycProtectedRoute>],
  ['/account/withdraw/account', () => <KycProtectedRoute><WithdrawalAccount /></KycProtectedRoute>],
  ['/account/messages', () => <Messages />],
  ['/account/activity', () => <AccountActivity />],
  ['/account/game-history', () => <GameHistory />],
  ['/account/coupons', () => <BetCoupons />],
  ['/account/loss-bonus', () => <LossBonus />],
  ['/account/my-bonuses', () => <MyBonuses />],
];

const SpinlabLegacyRoute = ({ render }) => {
  const themeId = useActiveTheme();
  if (themeId !== 'spinlab') return <CatchAllRoute />;
  return <PublicLayout><SpinlabAccountFrame>{render()}</SpinlabAccountFrame></PublicLayout>;
};

// Tanımsız adresler. Kurulum kısıtlıyken duyuru ekranı çizilir — kilit
// "ana sayfa kapalı ama /rastgele-adres açık" gibi bir boşluk bırakmamalıdır.
//
// Kalan her durumda 404 sayfası çizilir. Yönetim paneli ayrı bir alan adına
// kilitlendiğinde ana sitedeki ``/admin`` yolu da buraya düşer ve olmayan
// herhangi bir adresle **aynı** yanıtı verir: panelin varlığına dair iz
// bırakmaz. Eskiden boş ekran çiziliyordu, ziyaretçi siteyi bozuk sanıyordu.
// Spinlab şemasının kendi adresleri (kaynak temanın yol düzeni). Yalnızca
// şema Spinlab iken çizilir; diğer şemalarda bu adresler önceki gibi
// tanımsızdır (404) — başka bir şemanın davranışı değişmez.
const SPINLAB_PATHS = [
  '/casino', '/casino/*', '/vip', '/bonus', '/challenges', '/leaderboards',
  '/account/*', '/sports', '/legal/*',
];

const SpinlabOnlyRoute = () => {
  const themeId = useActiveTheme();
  if (themeId !== 'spinlab') return <CatchAllRoute />;
  return <PublicLayout><SpinlabRoute /></PublicLayout>;
};

// Canlı Betrun'un modülü BinoGaming'de olmayan sayfaları (Çapa, Sponsorluk;
// Betrun'da verileri 0). Canlıdaki boş durum sayfası birebir çizilir, eski
// bağlantı ve Telegram gönderileri 404'e düşmez; yalnız Betrun şemasında.
const BETRUN_LEGACY_PATHS = { '/capa': 'capa', '/sponsorluk': 'sponsorluk' };

const CatchAllRoute = () => {
  const settings = useSettings();
  const { pathname } = useLocation();
  const themeId = useActiveTheme();
  const legacyPage = themeId === 'betrun' && BETRUN_LEGACY_PATHS[pathname.replace(/\/+$/, '')];
  if (legacyPage) return <PublicLayout><BetrunLegacyPage page={legacyPage} /></PublicLayout>;
  // Panel bu adreste duruyorsa oradaki tanımsız adres yöneticinin sorunudur;
  // portal kabuğuyla 404 çizmek panelin içinde yabancı durur.
  if (pathname.startsWith('/admin') && adminRoutesVisible(settings)) return null;
  return <PublicLayout><NotFound /></PublicLayout>;
};

/* Promosyon Kodu hesap menüsünün bir maddesidir ama rotası hesap bölümünün
 * dışındadır (ziyaretçiye de açıktır). Giriş yapmış üye sayfayı hesap
 * kabuğunun **içinde** görür: menüden açtığı sayfada menü kayboluyor ve geri
 * dönmenin yolu kalmıyordu. Kural bir süre tek bir şemaya bağlıydı; kaybolan
 * menü şemadan bağımsız bir çıkmazdır. Ziyaretçi sayfayı kabuksuz görür —
 * hesap kabuğu ona "Giriş Gerekli" derdi.
 */
/* KYC: canlı Betrun'da ayrı sayfadır ("KYC Doğrulandı" kartı ya da başvuru
 * formu); diğer şemalarda KYC profil sayfasındadır ve eski bağlantı oraya gider. */
const KycRoute = () => (
  useActiveTheme() === 'betrun' ? <KycVerification /> : <Navigate to="/hesap/profil" replace />
);

const PromoCodeRoute = () => {
  const { user, ready } = useAuth();
  const themeId = useActiveTheme();
  const settings = useSettings();
  // Spinlab'da promosyon kodu yalnız Bonuslar sayfasındadır (10.10.2026):
  // eski bağlantı oraya gider, ayrı bir kod sayfası çizilmez.
  if (themeId === 'spinlab') return <Navigate to="/bonus" replace />;
  // Promosyon kodu üyeye özeldir: paylaşılan bağlantıyla gelen misafir sayfayı
  // görmez, ana sayfada giriş penceresine yönlenir.
  // Betrun'da misafir sayfayı hesap kabuğunun "Giriş Gerekli" ekranıyla görür
  // (canlı Betrun, `promo_code_guest_view` kapalı).
  // Karar şema bilinince verilir: ilk ziyarette önbellekte şema yoktur ve
  // ayarlar gelmeden varsayılan şemaya göre yönlendirilirdi.
  if (!user) {
    if (!ready || !settings.settingsLoaded) return null;
    if (themeId !== 'betrun') return <Navigate to="/" replace state={{ auth: 'login', from: '/promosyon-kodu' }} />;
  }
  return (
    <PublicLayout>
      <AccountLayout><PromoCode embedded /></AccountLayout>
    </PublicLayout>
  );
};

/**
 * KYC istemeyen bir sayfayı doğrulanmamış üyeden korur.
 *
 * ``deposit`` sayfalarında kapı yalnızca **otomatik** KYC modunda durur:
 * orada her hesap doğrulanmış açılır, doğrulanmamış olan yöneticinin geri
 * aldığı hesaptır. Kayıtta kimlik sorulmayan manuel modda doğrulanmamış hesap
 * normaldir ve para yatırması engellenmez — kural backend'de de aynıdır
 * (finance_router `/deposit`). Çekim her iki modda da doğrulama ister.
 */
const KycProtectedRoute = ({ children, deposit = false }) => {
  const { user, ready } = useAuth();
  const settings = useSettings();
  if (!ready) return null;
  if (deposit && settings.kyc_auto_approve === false) return children;
  // Kimlik ve telefon kayıtta doğrulanır; oyuncu ekranında ayrı bir KYC adımı
  // yoktur. Doğrulanmamış hesap yalnızca yöneticinin geri aldığı hesaptır:
  // sayfa yerine bilgi çizilir (bkz. AccessBlockedNotice).
  if (!user?.kyc_verified) {
    return (
      <AccessBlockedNotice
        title="Hesap doğrulaması bekleniyor"
        description="Bu işlem için hesabınızın doğrulanmış olması gerekir."
      />
    );
  }
  return children;
};

/**
 * Rotalar ayarlara bağlıdır: yönetim paneli ayrı bir alan adına taşındıysa
 * portal ile panel aynı pakette olsa da **aynı adreste tanımlanmaz**
 * (bkz. lib/adminDomain.js). Panel adresinde portalın hiçbir sayfası,
 * ana sitede ise kilit onaylandıktan sonra /admin yolları yoktur.
 *
 * Ayarlar gelene kadar hiçbir rota çizilmez: ayrım bilinmeden çizilen kabuk
 * bir kare sonra değişir ve oyuncu portalın açılıp kapandığını görürdü.
 */
const AppRoutes = () => {
  const settings = useSettings();
  const themeId = useActiveTheme();
  const { pathname } = useLocation();
  if (!settings.settingsLoaded) return null;
  // Sunucuya hiç ulaşılamıyorsa (kod güncellemesi sırasında servis yeniden
  // başlar, ters vekil 502/503 döner) elde ayar yoktur: rotalar çizilse de
  // site boş bir kabuk olarak açılır ve oyuncu "site bozuldu" der. Bunun
  // yerine ne olduğunu söyleyen bir ekran çizilir; bağlantı arka planda
  // yoklanır ve sunucu döndüğü anda site kendiliğinden açılır.
  if (settings.serverUnreachable) return <ServiceUnavailableScreen />;
  // Bekleyen ya da kayıtsız alan adı (Ortak Alan Adları): site hiç çizilmez.
  if (settings.domainNotFound) return <DomainNotFoundScreen />;
  // Affiliates paneli yeni bir alan adına taşındıysa eski adres yeni adrese gider.
  // Bayi paneli kendi alan adındaysa oyun sitesinde panel yolu yoktur: /ortak oraya gider.
  const partnerMoved = partnerRedirectUrl(settings) || partnerPathRedirectUrl(settings);
  if (partnerMoved) {
    window.location.replace(partnerMoved);
    return null;
  }
  // Bayi paneli ayrı bir alan adına bağlandıysa o adresin her yolu bayi
  // panelidir: site kabuğu, özel kod ve karşılama penceresi çizilmez.
  if (isPartnerHost(settings)) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="*" element={<PartnerPortal />} />
        </Routes>
      </Suspense>
    );
  }
  const adminRoutes = adminRoutesVisible(settings);
  const portal = portalVisible(settings);
  const partnerPath = partnerPanelPath(settings);
  // Spinlab portalında kabuk rotaların dışında, bir kez kurulur. Panel, bayi
  // paneli, builder önizlemesi ve bakım ekranı kabuksuzdur.
  const persistentSpinlab = portal && themeId === 'spinlab' && !settings.maintenance_mode
    && !(adminRoutes && pathname.startsWith('/admin'))
    && !pathname.startsWith(partnerPath) && pathname !== DEFAULT_PARTNER_PATH
    && pathname !== '/builder-onizleme';

  const routes = (
    <Suspense fallback={null}>
      <Routes>
            {/* Admin routes (no public header/footer) */}
            {adminRoutes && (
              <Route
              path="/admin/login"
              element={<Navigate to="/" replace state={{ auth: 'login' }} />}
            />
            )}
            {adminRoutes && (
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              {/* Sol menüdeki "Yakında" placeholder başlıkları (Betshop, CRM,
                  gelişmiş raporlar …) buraya düşer; bölüm adı ?b= ile gelir. */}
              <Route path="yakinda" element={<Navigate to="/admin" replace />} />
              <Route path="users" element={<PlayerSearch />} />
              <Route path="players/conversion" element={<ReportPage key="player-conversion" reportKey="player-conversion" />} />
              <Route path="players/kpi" element={<ReportPage key="player-kpi" reportKey="player-kpi" />} />
              <Route path="players/self-excluded" element={<ReportPage key="self-excluded" reportKey="self-excluded" />} />
              <Route path="players/limits" element={<ReportPage key="limit-requests" reportKey="limit-requests" />} />
              <Route path="reports/:key" element={<ReportPage />} />
              {/* Oyuncu profili (BetConstruct düzeni): sekme ve bölüm adreste durur;
                  eski /transactions ve /games adresleri Raporlar sekmesine yönlenir. */}
              <Route path="users/:userId" element={<PlayerProfile />} />
              <Route path="users/:userId/:tab" element={<PlayerProfile />} />
              <Route path="users/:userId/:tab/:section" element={<PlayerProfile />} />
              {/* KYC onayları artık oyuncunun profil sayfasındadır; eski
                  bağlantılar kırılmasın diye üye listesine yönlendirilir. */}
              <Route path="kyc" element={<Navigate to="/admin/users" replace />} />
              {/* Finans Geçmişi ve Manuel İşlemler, Para Çekme Talepleri ile aynı
                  düzendedir (admin/payments/PaymentTransactions.jsx). */}
              <Route path="finance" element={<PaymentTransactions key="finance" type="finance" />} />
              <Route path="manual-transactions" element={<PaymentTransactions key="manual" type="manual" />} />
              {/* Finansal İnceleme kaldırıldı (29.10.2026); eski adres Finans Geçmişi'ne yönlenir. */}
              <Route path="finance/reviews" element={<Navigate to="/admin/finance" replace />} />
              {/* Oyuncudan oyuncuya bahşiş (services/player_tips.py; varsayılan kapalı). */}
              <Route path="finance/tips" element={<TipsPage />} />
              {/* Bekleyen Kazançlar (services/pending_wins.py): yazılmamış sağlayıcı kazancının kararı. */}
              <Route path="finance/pending-wins" element={<PendingWinsPage />} />
              {/* Aşağıdaki dört adres CMS'e taşındı; eski bağlantılar/yer
                  imleri kırılmasın diye yeni yola yönlendirilir (geriye
                  dönük uyumluluk — bkz. "CMS arkasındaki ayarların
                  yeniden organizasyonu"). */}
              <Route path="payment-methods" element={<Navigate to="/admin/payments/methods" replace />} />
              <Route path="payments" element={<Navigate to="/admin/payments/deposits" replace />} />
              <Route path="payments/deposits" element={<PaymentTransactions key="deposit" type="deposit" />} />
              <Route path="payments/withdrawals" element={<PaymentTransactions key="withdraw" type="withdraw" />} />
              {/* Ödeme günlükleri kaldırıldı (29.10.2026); eski adresler Finansal'a yönlenir. */}
              <Route path="payments/deposit-log" element={<Navigate to="/admin/payments/deposits" replace />} />
              <Route path="payments/withdraw-log" element={<Navigate to="/admin/payments/withdrawals" replace />} />
              <Route path="payments/methods" element={<PaymentMethods />} />
              <Route path="payments/orders" element={<PaymentOrders />} />
              {/* Bildirim Ayarları kaldırıldı; şablonlar Mesajlar grubundadır. */}
              <Route path="finance-notify" element={<Navigate to="/admin/messages/email-templates" replace />} />
              <Route path="bonus-engine" element={<BonusList />} />
              <Route path="bonus-engine/:id" element={<BonusList />} />
              <Route path="bonus-claims" element={<BonusClaims />} />
              <Route path="games" element={<Navigate to="/admin/cms/casino" replace />} />
              <Route path="live-casino-control" element={<Navigate to="/admin/cms/casino" replace />} />
              <Route path="sports-bets" element={<SportsBets />} />
              {/* Hazır spor kitabı kuponunun kimliği sağlayıcıya göre
                  benzersizdir, bizim kupon numaramız değildir; bu yüzden
                  sağlayıcı adı yolun parçasıdır ve sabit ``hosted`` ekiyle
                  sitenin kendi kupon rotasından ayrılır. */}
              <Route
                path="sports-bets/hosted/:provider/:betId"
                element={<SportsbookBetDetail />}
              />
              <Route path="sports-bets/:couponId" element={<SportsBetDetail />} />
              {/* SportsBook Yönetimi (BetConstruct düzeni). */}
              <Route path="sportsbook/liabilities" element={<Liabilities />} />
              <Route path="sportsbook/super-bet" element={<SuperBets />} />
              {/* Sportsbook ayarları CMS › Casino › Spor & Cashout'ta yönetilir (29.10.2026, sistem sahibi). */}
              <Route path="sportsbook/settings" element={<Navigate to="/admin/cms/casino/sports" replace />} />
              <Route path="sportsbook/live-reservations" element={<LiveReservations />} />
              <Route path="sportsbook/prematch-admin" element={<SubscriptionAdmin key="prematch" mode="prematch" />} />
              <Route path="sportsbook/live-admin" element={<SubscriptionAdmin key="live" mode="live" />} />
              <Route path="sportsbook/market-groups" element={<MarketGroups />} />
              <Route path="sportsbook/market-reservation" element={<MarketReservation />} />
              <Route path="sportsbook/orders" element={<Orders />} />
              <Route path="sportsbook/limits/global" element={<GlobalLimits />} />
              <Route path="sportsbook/limits/sports" element={<SportLimits />} />
              <Route path="sportsbook/casino-limits" element={<CasinoLimits />} />
              <Route path="sportsbook/popular" element={<PopularManage />} />
              <Route path="sportsbook/popular-matches" element={<PopularMatches />} />
              {/* Mesajlar */}
              <Route path="messages/email-templates" element={<EmailTemplates />} />
              <Route path="messages/list" element={<MessageList />} />
              <Route path="messages/list/new" element={<NewMessage />} />
              <Route path="messages/sms-templates" element={<SmsTemplates />} />
              <Route path="bonuses" element={<Navigate to="/admin/cms/promotions" replace />} />
              <Route path="promo-codes" element={<PromoCodes />} />
              {/* Etkinlikler: Şans Çarkı, Çekilişler, Tahmin Etkinlikleri tek sayfada (29.10.2026). */}
              <Route path="etkinlikler" element={<EventsPage />} />
              <Route path="wheel" element={<Navigate to="/admin/etkinlikler?sekme=cark" replace />} />
              {/* Freespin Kontrol menüden kaldırıldı; ayrıntılar Promosyon Kodları
                  satırındaki göz simgesiyle açılan penceredir. */}
              <Route path="freespin" element={<Navigate to="/admin/promo-codes" replace />} />
              <Route path="cekilisler" element={<Navigate to="/admin/etkinlikler?sekme=cekilis" replace />} />
              <Route path="tahmin-etkinlikleri" element={<Navigate to="/admin/etkinlikler?sekme=tahmin" replace />} />
              <Route path="affiliate" element={<Navigate to="/admin/affiliates" replace />} />
              <Route path="slider" element={<Navigate to="/admin/cms/banners" replace />} />
              <Route path="search" element={<CallRequests />} />
              <Route path="footer" element={<Navigate to="/admin/cms/menus" replace />} />
              <Route path="footer-pages" element={<Navigate to="/admin/cms/pages" replace />} />
              {/* "Görünüm & Ayarlar" bölümünün tamamı CMS'e taşındı; eski
                  adresler yeni CMS yollarına yönlendirilir (geriye dönük
                  uyumluluk). */}
              <Route path="merit-side-panel" element={<Navigate to="/admin/cms/menus?menu=left" replace />} />
              <Route path="ana-sayfa-duzeni" element={<Navigate to="/admin/cms/banners" replace />} />
              <Route path="karsilama-penceresi" element={<Navigate to="/admin/cms/banners?view=popups" replace />} />
              {/* Eski Araçlar › Site Ayarları kaldırıldı; bölümleri yeni
                  yerlerindedir (bkz. admin/MovedSettingsPages.jsx). Eski
                  adresler hata vermez, yeni yerlere yönlenir. */}
              <Route path="settings" element={<Navigate to="/admin/partner-settings" replace />} />
              <Route path="site-settings" element={<Navigate to="/admin/partner-settings" replace />} />
              <Route path="footer-settings" element={<Navigate to="/admin/cms/menus?menu=footer" replace />} />
              <Route path="side-panel" element={<Navigate to="/admin/cms/menus?menu=left" replace />} />
              <Route path="finance/integrations" element={<PaymentIntegrationPage />} />
              <Route path="backup" element={<BackupPage />} />
              {/* Toplu Mail kaldırıldı (29.10.2026); eski adres Mesaj Listesi'ne yönlenir. */}
              <Route path="messages/bulk-mail" element={<Navigate to="/admin/messages/list" replace />} />

              {/* ── CMS bölümü ──────────────────────────────────────────────
                  Ayrı bir kabuk: AdminLayout bu yollarda ana menü yerine CMS
                  menüsünü çizer (bkz. lib/cmsNavigation.js). Gerçek maddeler
                  mevcut sayfa bileşenlerini yeniden kullanır; BetConstruct'a
                  özgü olup BHS'te karşılığı olmayan maddeler CmsPlaceholder
                  ("Yakında") gösterir. Eski `/admin/...` adresleri de korunur
                  (geriye dönük uyumluluk). */}
              <Route path="cms" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/media" element={<MediaPage />} />
              {/* Açılır pencereler Bannerlar'ın içindedir (29.10.2026). */}
              <Route path="cms/popups" element={<Navigate to="/admin/cms/banners?view=popups" replace />} />
              <Route path="cms/menus" element={<MenusPage />} />
              <Route path="cms/menus/side" element={<Navigate to="/admin/cms/menus?menu=side" replace />} />
              <Route path="cms/menus/footer" element={<Navigate to="/admin/cms/menus?menu=footer" replace />} />
              <Route path="cms/menus/header" element={<Navigate to="/admin/cms/menus?menu=header" replace />} />
              <Route path="cms/blocks" element={<Navigate to="/admin/cms/banners" replace />} />
              <Route path="cms/banners" element={<BannersPage />} />
              <Route path="cms/promotions" element={<PromotionsPage />} />
              <Route path="cms/pages" element={<PagesPage />} />
              {/* Header Info Bannerlar'ın içindedir (29.10.2026). */}
              <Route path="cms/notifications" element={<Navigate to="/admin/cms/banners?view=notifications" replace />} />
              <Route path="cms/stories" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/css/web" element={<CssPage target="web" />} />
              <Route path="cms/css/mobile" element={<CssPage target="mobile" />} />
              <Route path="cms/seo/pages" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/seo/games" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/seo/casino-categories" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/seo/live-casino-categories" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/seo/integrations" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/seo/robots" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/logo/web-mobile" element={<LogoWebPage />} />
              {/* Uygulama logoları Logo ve İnfo sayfasındadır (29.10.2026). */}
              <Route path="cms/logo/applications" element={<Navigate to="/admin/cms/logo/web-mobile" replace />} />
              <Route path="cms/bonuses/campaigns" element={<Navigate to="/admin/bonus-engine" replace />} />
              <Route path="cms/bonuses/loyalty" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/bonuses/beneficiary-cards" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/bonuses/exchange-shop" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/bonuses/requests" element={<Navigate to="/admin/cms/media" replace />} />
              <Route path="cms/payments" element={<PaymentsLogoPage />} />
              <Route path="cms/domains" element={<Navigate to="/admin/partner-domains" replace />} />
              {/* Görünüm & Ayarlar — mevcut sayfalar CMS kabuğunda. */}
              <Route path="cms/home-layout" element={<Navigate to="/admin/cms/banners" replace />} />
              <Route path="cms/footer-settings" element={<Navigate to="/admin/cms/menus?menu=footer" replace />} />
              <Route path="cms/side-panel" element={<Navigate to="/admin/cms/menus?menu=left" replace />} />
              <Route path="cms/theme" element={<ThemePage />} />
              {/* CMS › Spinlab sayfası kaldırıldı (29.10.2026, sistem sahibi). */}
              <Route path="cms/spinlab" element={<Navigate to="/admin/cms/theme" replace />} />
              <Route path="cms/settings" element={<Navigate to="/admin/partner-settings" replace />} />
              <Route path="cms/casino/*" element={<CasinoPage />} />
              <Route path="cms/page-builder" element={<PageBuilder />} />

              {/* Affiliates: ayrı sekmede açılan tam ekran kabuk (BetConstruct
                  Affiliates düzeni, bkz. admin/affiliates/gene ve AdminLayout). */}
              <Route path="affiliates" element={<AffDashboard />} />
              <Route path="affiliates/users" element={<AffUsers />} />
              <Route path="affiliates/users/:aid" element={<AffUserDetail />} />
              <Route path="affiliates/users/:aid/preview" element={<AffPreview />} />
              <Route path="affiliates/groups" element={<AffGroups />} />
              <Route path="affiliates/deleted" element={<AffDeleted />} />
              <Route path="affiliates/transferred-users" element={<AffTransfers kind="affiliate" />} />
              <Route path="affiliates/players" element={<AffPlayers view="all" />} />
              <Route path="affiliates/players/added" element={<AffPlayers view="added" />} />
              <Route path="affiliates/players/blocked" element={<AffPlayers view="blocked" />} />
              <Route path="affiliates/players/transferred" element={<AffTransfers kind="player" />} />
              <Route path="affiliates/links" element={<AffLinks />} />
              <Route path="affiliates/sources" element={<AffSources />} />
              <Route path="affiliates/landing" element={<AffLanding />} />
              <Route path="affiliates/media" element={<AffMedia />} />
              <Route path="affiliates/news" element={<AffNews />} />
              <Route path="affiliates/feed" element={<AffFeed />} />
              <Route path="affiliates/withdraws" element={<AffWithdraws />} />
              <Route path="affiliates/reports" element={<Navigate to="/admin/affiliates/reports/general" replace />} />
              <Route path="affiliates/reports/:name" element={<AffReports />} />
              <Route path="affiliates/filters" element={<AffFilters />} />
              <Route path="affiliates/newsletter" element={<AffNewsletter />} />
              <Route path="affiliates/files" element={<AffFiles />} />
              <Route path="affiliates/config" element={<AffConfig />} />
              <Route path="affiliates/config/:section" element={<AffConfig />} />
              <Route path="affiliates/manage" element={<Navigate to="/admin/affiliates/users" replace />} />
              <Route path="crm/*" element={<Suspense fallback={null}><CrmRoutes /></Suspense>} />

              <Route path="security" element={<AdminSecurity />} />
              <Route path="audit-log" element={<AuditLog />} />
              {/* Kullanıcı Yönetimi: backoffice hesapları, roller, giriş geçmişi. */}
              <Route path="staff" element={<StaffUsers />} />
              <Route path="staff/:userId" element={<StaffUserDetail />} />
              <Route path="roles" element={<StaffRoles />} />
              <Route path="roles/:roleKey" element={<StaffRoleDetail />} />
              <Route path="login-history" element={<LoginHistory />} />
              {/* Alan Adları, Ortak Alan Adları ile birleştirildi. */}
              <Route path="addresses" element={<Navigate to="/admin/partner-domains" replace />} />
              <Route path="partner-settings" element={<PartnerSettings />} />
              <Route path="partner-domains" element={<PartnerDomains />} />
              {/* Kritik Alarmlar artık Sistem & İstekler sayfasının sekmesidir (29.10.2026). */}
              <Route path="kritik-alarmlar" element={<Navigate to="/admin/technical?sekme=alarmlar" replace />} />
              {/* Oyun Açılış İzleme CMS › Casino › Analiz altına taşındı (29.10.2026). */}
              <Route path="oyun-acilis" element={<Navigate to="/admin/cms/casino/game-launch" replace />} />
              <Route path="technical" element={<Technical />} />
            </Route>
            )}

            {/* İş ortağı portalı: panelden bağımsız giriş ekranı ve yalnızca
                ortağın kendi verisini gösteren salt okunur panel (site
                kabuğu çizilmez). */}
            {/* Bayi paneli: adresi Affiliates → Yapılandırma'dan seçilir.
                Yol değiştirildiyse eski /ortak yer imleri yeni yola gider. */}
            {portal && <Route path={`${partnerPath}/*`} element={<PartnerPortal />} />}
            {portal && partnerPath !== DEFAULT_PARTNER_PATH && (
              <Route path={DEFAULT_PARTNER_PATH} element={<Navigate to={partnerPath} replace />} />
            )}

            {/* Public site — panel adresinde portal hiç çizilmez */}
            {portal && <Route path="/" element={<HomeRoute />} />}
            <Route path="/spor" element={<SportsRoute />} />
            <Route path="/slotlar" element={<CatalogRoute category="slot"><Slots /></CatalogRoute>} />
            <Route path="/canli-casino" element={<CatalogRoute category="live-casino"><LiveCasino /></CatalogRoute>} />
            <Route path="/crash" element={<CatalogRoute category="crash"><Crash /></CatalogRoute>} />
            {/* CMS › Menüler "Require Login": ziyaretçiyi giriş penceresine götürür. */}
            <Route path="/giris" element={<Navigate to="/" replace state={{ auth: 'login' }} />} />
            <Route path="/cark" element={<PublicLayout><Wheel /></PublicLayout>} />
            <Route path="/cekilis" element={<PublicLayout><Raffle /></PublicLayout>} />
            <Route path="/etkinlik" element={<PublicLayout><Prediction /></PublicLayout>} />
            <Route path="/etkinlik/:slug" element={<PublicLayout><Prediction /></PublicLayout>} />
            <Route path="/promosyonlar" element={<PublicLayout><Promotions /></PublicLayout>} />
            <Route path="/promosyon-kodu" element={<PromoCodeRoute />} />
            <Route path="/sayfa/:slug" element={<PublicLayout><FooterPage /></PublicLayout>} />
            {/* Page Builder: yayındaki sayfa ve editör önizlemesi (CMS › Page Builder). */}
            <Route path="/s/:slug" element={<PublicLayout><BuilderPage /></PublicLayout>} />
            <Route path="/builder-onizleme" element={<BuilderPreview />} />
            {/* Oyunun kendi sayfası: yalnızca `gamePage` taşıyan şemada (Betrun)
                açılır, diğerlerinde ana sayfaya yönlenir (bkz. lib/gamePage.js). */}
            <Route path="/oyun/:gameId" element={<PublicLayout><GamePage /></PublicLayout>} />
            <Route path="/hesap" element={<PublicLayout><AccountLayout /></PublicLayout>}>
              <Route path="profil" element={<ThemedAccountRoute page="profil"><Profile /></ThemedAccountRoute>} />
              <Route path="guvenlik" element={<ThemedAccountRoute page="guvenlik"><Security /></ThemedAccountRoute>} />
              <Route path="kyc" element={<ThemedAccountRoute page="kyc"><KycRoute /></ThemedAccountRoute>} />
              <Route
                path="para-yatir"
                element={<ThemedAccountRoute page="para-yatir"><KycProtectedRoute deposit><Deposit /></KycProtectedRoute></ThemedAccountRoute>}
              />
              <Route
                path="para-yatir/odeme"
                element={(
                  <ThemedAccountRoute page="para-yatir/odeme">
                    <KycProtectedRoute deposit><DepositPayment /></KycProtectedRoute>
                  </ThemedAccountRoute>
                )}
              />
              <Route
                path="para-cek"
                element={<ThemedAccountRoute page="para-cek"><KycProtectedRoute><Withdraw /></KycProtectedRoute></ThemedAccountRoute>}
              />
              <Route
                path="para-cek/hesap"
                element={<ThemedAccountRoute page="para-cek/hesap"><KycProtectedRoute><WithdrawalAccount /></KycProtectedRoute></ThemedAccountRoute>}
              />
              <Route path="mesajlar" element={<ThemedAccountRoute page="mesajlar"><Messages /></ThemedAccountRoute>} />
              {/* Hesap Hareketleri: panelden elle yapılan bakiye düzenlemeleri.
                  Yatırım, çekim ve oyun turları kendi sayfalarındadır; eski
                  "İşlemlerim" adresi bu sayfaya bağlandı. */}
              <Route path="islemler" element={<ThemedAccountRoute page="islemler"><AccountActivity /></ThemedAccountRoute>} />
              <Route path="oyun-gecmisi" element={<ThemedAccountRoute page="oyun-gecmisi"><GameHistory /></ThemedAccountRoute>} />
              <Route path="bahis-kuponlari" element={<ThemedAccountRoute page="bahis-kuponlari"><BetCoupons /></ThemedAccountRoute>} />
              <Route path="kayip-bonusu" element={<ThemedAccountRoute page="kayip-bonusu"><LossBonus /></ThemedAccountRoute>} />
              <Route path="bonuslarim" element={<ThemedAccountRoute page="bonuslarim"><MyBonuses /></ThemedAccountRoute>} />
              <Route path="affiliate" element={<AffiliateAccessRoute />} />
            </Route>
            {portal && SPINLAB_LEGACY_ACCOUNT.map(([path, render]) => (
              <Route key={path} path={path} element={<SpinlabLegacyRoute render={render} />} />
            ))}
            {portal && SPINLAB_PATHS.map((path) => (
              <Route key={path} path={path} element={<SpinlabOnlyRoute />} />
            ))}
            {/* Panel adresinde kök adres panele yönlenir; portalın hiçbir
                sayfası burada tanımlı değildir. */}
            <Route
              path="*"
              element={portal ? <CatchAllRoute /> : <Navigate to="/admin" replace />}
            />
      </Routes>
    </Suspense>
  );

  return (
    <>
      {portal && <CustomCodeInjector />}
      {/* Panelden yönetilen karşılama penceresi (bkz. lib/welcomePopup.js). */}
      {portal && <WelcomePopup />}
      {portal && <PlayerMessageAlerts />}
      {/* Pasif domaine gelen ziyaretçi ana domaine (Alan Adları). */}
      {portal && <DomainRedirect />}
      {/* CRM anlık bildirim (OneSignal) aboneliği. */}
      {portal && <PushSubscribe />}
      {/* Panelden yazılan CSS'in "yalnızca bu sayfada" demesinin yolu:
          <body data-bhs-page="…"> (bkz. lib/domHooks.js). */}
      <PageMarker />
      {/* Page Builder canlı sayfa katmanı: vitrin sayfalarına eklenen bloklar ve
          gizlenen tema bölümleri (components/builder/LayerHost.jsx). */}
      {portal && <Suspense fallback={null}><BuilderLayerHost /></Suspense>}
      {/* Şemanın mobil yakınlaştırma kilidi (bkz. lib/viewportLock.js). */}
      <ViewportLock settings={settings} />
      {persistentSpinlab ? (
        <PersistentShell.Provider value>
          <SpinlabLayout persistent extras={<LiveChatSupport />}>{routes}</SpinlabLayout>
        </PersistentShell.Provider>
      ) : routes}
    </>
  );
};

function App() {
  useEffect(() => {
    client.get('/').catch(() => {});
    // ?ref=XXXXX ile gelen ziyaretçinin referans kodunu yakala
    captureRef();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <SettingsProvider>
        {/* React Router 7 gezinmeyi geçiş (transition) içinde yapar: tembel
            yüklenen bir sayfanın paketi inerken eski ekran yerinde kalır. */}
        <BrowserRouter>
          <BrowserProtection />
          <AppRoutes />
          <Toaster />
          <Suspense fallback={null}><BonusWalletPanel /></Suspense>
        </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
